import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { db } from '@/lib/firebase';
import { collection, query, where, getDocs, orderBy, deleteDoc, doc } from 'firebase/firestore';
import { FileText, Download, Trash2, Upload, Search, FolderOpen, File, Image, FileSpreadsheet } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

interface Document {
  id: string;
  name: string;
  type: string;
  category: 'contract' | 'report' | 'invoice' | 'legal' | 'other';
  size: number;
  uploadedAt: Date;
  mandateId?: string;
  mandateName?: string;
  clientId?: string;
  clientName?: string;
  url?: string;
}

const mockDocuments: Document[] = [
  {
    id: "doc-1",
    name: "Due Diligence Report - Tech Solutions.pdf",
    type: "application/pdf",
    category: "report",
    size: 2548000,
    uploadedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
    mandateId: "mock-mandate-1",
    mandateName: "Due Diligence Juridique - Tech Solutions",
    clientId: "mock-client-1",
    clientName: "Jean-Pierre Martin",
  },
  {
    id: "doc-2",
    name: "Contrat de Service - Mandat Fiscal.pdf",
    type: "application/pdf",
    category: "contract",
    size: 1250000,
    uploadedAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000),
    mandateId: "mock-mandate-2",
    mandateName: "Planification Fiscale",
    clientId: "mock-client-2",
    clientName: "Sophie Tremblay",
  },
  {
    id: "doc-3",
    name: "Facture - Évaluation Financière.pdf",
    type: "application/pdf",
    category: "invoice",
    size: 450000,
    uploadedAt: new Date(Date.now() - 12 * 24 * 60 * 60 * 1000),
    mandateId: "mock-mandate-3",
    mandateName: "Évaluation Financière - Portfolio",
    clientId: "mock-client-3",
    clientName: "Marc Dubois",
  },
  {
    id: "doc-4",
    name: "Analyse Contrats - Annexe A.docx",
    type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    category: "legal",
    size: 890000,
    uploadedAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
    mandateId: "mock-mandate-1",
    mandateName: "Due Diligence Juridique - Tech Solutions",
    clientId: "mock-client-1",
    clientName: "Jean-Pierre Martin",
  },
  {
    id: "doc-5",
    name: "Données Financières - Portfolio.xlsx",
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    category: "report",
    size: 1850000,
    uploadedAt: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000),
    mandateId: "mock-mandate-3",
    mandateName: "Évaluation Financière - Portfolio",
    clientId: "mock-client-3",
    clientName: "Marc Dubois",
  },
];

const translations = {
  en: {
    title: "Documents",
    subtitle: "All your mandate-related documents and files",
    uploadDocument: "Upload Document",
    searchDocuments: "Search documents...",
    allCategories: "All Categories",
    contract: "Contracts",
    report: "Reports",
    invoice: "Invoices",
    legal: "Legal",
    other: "Other",
    noDocuments: "No documents yet",
    uploadFirst: "Upload your first document to get started",
    download: "Download",
    delete: "Delete",
    mandate: "Mandate",
    client: "Client",
    category: "Category",
    size: "Size",
    uploaded: "Uploaded",
    documentDeleted: "Document Deleted",
    documentDeletedDesc: "The document has been removed successfully",
    error: "Error",
    errorDesc: "Failed to delete document",
    loading: "Loading documents...",
  },
  fr: {
    title: "Documents",
    subtitle: "Tous vos documents liés aux mandats",
    uploadDocument: "Téléverser Document",
    searchDocuments: "Rechercher documents...",
    allCategories: "Toutes Catégories",
    contract: "Contrats",
    report: "Rapports",
    invoice: "Factures",
    legal: "Juridique",
    other: "Autres",
    noDocuments: "Aucun document pour le moment",
    uploadFirst: "Téléversez votre premier document pour commencer",
    download: "Télécharger",
    delete: "Supprimer",
    mandate: "Mandat",
    client: "Client",
    category: "Catégorie",
    size: "Taille",
    uploaded: "Téléversé",
    documentDeleted: "Document Supprimé",
    documentDeletedDesc: "Le document a été supprimé avec succès",
    error: "Erreur",
    errorDesc: "Échec de la suppression du document",
    loading: "Chargement des documents...",
  },
};

export default function ConsultantDocuments() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const { toast } = useToast();
  const t = translations[language as keyof typeof translations] || translations.en;

  const [documents, setDocuments] = useState<Document[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState<string>("all");

  useEffect(() => {
    if (user) {
      loadDocuments();
    }
  }, [user]);

  const loadDocuments = async () => {
    if (!user) return;
    setIsLoading(true);
    try {
      const documentsQuery = query(
        collection(db, 'consultantDocuments'),
        where('consultantId', '==', user.email),
        orderBy('uploadedAt', 'desc')
      );
      const documentsSnapshot = await getDocs(documentsQuery);
      const documentsList = documentsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        uploadedAt: doc.data().uploadedAt?.toDate(),
      })) as Document[];
      setDocuments(documentsList.length > 0 ? documentsList : mockDocuments);
    } catch (error) {
      console.error('Error fetching documents:', error);
      setDocuments(mockDocuments);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteDocument = async (documentId: string) => {
    try {
      await deleteDoc(doc(db, 'consultantDocuments', documentId));
      setDocuments(documents.filter(d => d.id !== documentId));
      toast({ title: t.documentDeleted, description: t.documentDeletedDesc });
    } catch (error) {
      toast({ title: t.error, description: t.errorDesc, variant: 'destructive' });
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (date: any) => {
    const d = date instanceof Date ? date : new Date(date);
    return format(d, 'dd MMM yyyy', { locale: language === 'fr' ? fr : undefined });
  };

  const getFileIcon = (type: string) => {
    if (type.includes('pdf')) return <FileText className="h-8 w-8 text-red-500" />;
    if (type.includes('image')) return <Image className="h-8 w-8 text-blue-500" />;
    if (type.includes('spreadsheet') || type.includes('excel')) return <FileSpreadsheet className="h-8 w-8 text-green-500" />;
    return <File className="h-8 w-8 text-muted-foreground" />;
  };

  const getCategoryLabel = (category: string) => {
    return t[category as keyof typeof t] || category;
  };

  const getCategoryBadgeVariant = (category: string): "default" | "secondary" | "outline" => {
    switch (category) {
      case 'contract': return 'default';
      case 'report': return 'secondary';
      case 'invoice': return 'outline';
      default: return 'outline';
    }
  };

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         doc.mandateName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         doc.clientName?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterCategory === "all" || doc.category === filterCategory;
    return matchesSearch && matchesFilter;
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t.title}</h1>
          <p className="text-muted-foreground mt-2">{t.subtitle}</p>
        </div>
        <Button>
          <Upload className="mr-2 h-4 w-4" />
          {t.uploadDocument}
        </Button>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t.searchDocuments}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-full md:w-[180px]">
            <SelectValue placeholder={t.allCategories} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t.allCategories}</SelectItem>
            <SelectItem value="contract">{t.contract}</SelectItem>
            <SelectItem value="report">{t.report}</SelectItem>
            <SelectItem value="invoice">{t.invoice}</SelectItem>
            <SelectItem value="legal">{t.legal}</SelectItem>
            <SelectItem value="other">{t.other}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Documents List */}
      {filteredDocuments.length > 0 ? (
        <div className="space-y-3">
          {filteredDocuments.map((document) => (
            <Card key={document.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    {getFileIcon(document.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-sm truncate">{document.name}</h4>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <Badge variant={getCategoryBadgeVariant(document.category)}>
                            {getCategoryLabel(document.category)}
                          </Badge>
                          <span className="text-xs text-muted-foreground">{formatFileSize(document.size)}</span>
                          <span className="text-xs text-muted-foreground">•</span>
                          <span className="text-xs text-muted-foreground">{formatDate(document.uploadedAt)}</span>
                        </div>
                        {document.mandateName && (
                          <p className="text-xs text-muted-foreground mt-2">
                            {t.mandate}: {document.mandateName}
                          </p>
                        )}
                        {document.clientName && (
                          <p className="text-xs text-muted-foreground">
                            {t.client}: {document.clientName}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        <Button variant="outline" size="sm">
                          <Download className="h-4 w-4 mr-1" />
                          {t.download}
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleDeleteDocument(document.id)}
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          {t.delete}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FolderOpen className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold">{t.noDocuments}</h3>
            <p className="text-muted-foreground text-center mt-2 mb-4">
              {t.uploadFirst}
            </p>
            <Button>
              <Upload className="mr-2 h-4 w-4" />
              {t.uploadDocument}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
